import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { resolveLandingPath, companyHasInventory } from '@/lib/landing';

export type PersonaType = 'buyer' | 'seller' | 'buyer_seller';

/**
 * The company role is DERIVED from the capabilities the company's plan grants
 * (C-RL-02): `inventory.manage` => selling, `menus.browse` => buying. It is a
 * read-only cache on `companies.persona`, maintained by the database. There is
 * deliberately no write path from the client.
 */

export interface UserPersona {
  id: string;
  user_id: string;
  persona: PersonaType;
  is_primary: boolean;
  created_at: string;
}

export const PERSONA_LANDING_PAGES: Record<PersonaType, string> = {
  buyer: '/supplier-menus',
  // Sellers land on Your Desk: the brief is the daily trigger, and it links out to
  // inventory and menus from there.
  seller: '/desk',
  buyer_seller: '/desk',
};

export const PERSONA_LABELS: Record<PersonaType, { label: string; description: string }> = {
  buyer: {
    label: 'Buyer',
    description: 'Browse supplier menus and track listings you care about',
  },
  seller: {
    label: 'Seller',
    description: 'Publish inventory and send menus to buyers',
  },
  buyer_seller: {
    label: 'Buyer & Seller',
    description: 'Publish inventory and browse supplier menus',
  },
};

export const SELLER_PERSONAS: PersonaType[] = ['seller', 'buyer_seller'];
export const BUYER_PERSONAS: PersonaType[] = ['buyer', 'buyer_seller'];
export const ALL_PERSONAS: PersonaType[] = ['buyer', 'seller', 'buyer_seller'];

export const isSellerPersona = (persona: PersonaType | null | undefined): boolean =>
  persona === 'seller' || persona === 'buyer_seller';

export const isBuyerPersona = (persona: PersonaType | null | undefined): boolean =>
  persona === 'buyer' || persona === 'buyer_seller';

export const useUserPersona = () => {
  const { user } = useAuth();

  const query = useQuery({
    queryKey: ['userPersonas', user?.id],
    queryFn: async (): Promise<UserPersona[]> => {
      if (!user) return [];

      const { data, error } = await supabase
        .from('users')
        .select('company_id')
        .eq('id', user.id)
        .maybeSingle();

      if (error || !data?.company_id) {
        console.error('Error fetching user company:', error);
        return [];
      }

      const { data: company, error: companyError } = await supabase
        .from('companies')
        .select('id, persona')
        .eq('id', data.company_id)
        .maybeSingle();

      if (companyError || !company) {
        console.error('Error fetching company persona:', companyError);
        return [];
      }

      const raw = company.persona;
      const persona: PersonaType = ALL_PERSONAS.includes(raw as PersonaType) ? (raw as PersonaType) : 'buyer';
      return [
        {
          id: company.id,
          user_id: user.id,
          persona,
          is_primary: true,
          created_at: new Date().toISOString(),
        },
      ];
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  // Company admins can request a plan change; nobody can author the role itself.
  const adminQuery = useQuery({
    queryKey: ['isCompanyAdmin', user?.id],
    queryFn: async (): Promise<boolean> => {
      if (!user) return false;
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .eq('role', 'admin')
        .maybeSingle();
      if (error) {
        console.error('Error checking admin status:', error);
        return false;
      }
      return !!data;
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });

  const primaryPersona = query.data?.find((p) => p.is_primary)?.persona || null;
  const isCompanyAdmin = adminQuery.data ?? false;
  const companyId = query.data?.[0]?.id ?? null;

  /* Sellers go to Your Desk only once they have inventory — an empty brief is a wasted
     trigger. Resolved once per session and cached; until it resolves the persona
     default applies, so nothing waits on it. */
  const inventoryQuery = useQuery({
    queryKey: ['companyHasInventory', companyId],
    queryFn: () => companyHasInventory(companyId),
    enabled: !!companyId && isSellerPersona(primaryPersona),
    staleTime: 5 * 60 * 1000,
  });

  const getLandingPage = (): string =>
    resolveLandingPath(primaryPersona, inventoryQuery.data ?? null);

  return {
    personas: query.data || [],
    primaryPersona,
    isCompanyAdmin,
    isLoading: query.isLoading,
    refetch: query.refetch,
    getLandingPage,
  };
};
